import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import {
  FaCheck,
  FaClock,
  FaEnvelope,
  FaLocationDot,
  FaPhone,
  FaTriangleExclamation,
  FaWhatsapp,
} from "react-icons/fa6";
import Reveal from "./Reveal";
import { site, addressLine, whatsappUrl, mapsUrl } from "../data/site";
import { trackEvent } from "../lib/analytics";

// Configurable por variables de entorno (.env). Si no están definidas, se usan
// las credenciales actuales para no romper el envío.
const EMAILJS = {
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_7adih4b",
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_xgl22aj",
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "esCnAPZlT3c2vU2Sm",
};

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 " +
  "placeholder:text-slate-400 transition-colors focus:border-campo-500 focus:outline-none " +
  "focus:ring-2 focus:ring-campo-500/20";

const labelClass = "mb-1.5 block text-sm font-medium text-slate-700";

const Field = ({ id, label, required, children }) => (
  <div>
    <label htmlFor={id} className={labelClass}>
      {label}
      {required && (
        <span className="ml-0.5 text-campo-700" aria-hidden="true">
          *
        </span>
      )}
    </label>
    {children}
  </div>
);

const Contact = () => {
  const form = useRef();
  const [status, setStatus] = useState("idle"); // idle | sending | ok | error

  const sendEmail = (e) => {
    e.preventDefault();

    // Honeypot: los bots completan este campo oculto, las personas no.
    if (form.current.elements.website?.value) return;

    setStatus("sending");

    emailjs
      .sendForm(EMAILJS.serviceId, EMAILJS.templateId, form.current, EMAILJS.publicKey)
      .then(() => {
        setStatus("ok");
        trackEvent("generate_lead", { method: "form" });
        form.current.reset();
      })
      .catch(() => setStatus("error"));
  };

  const contactItems = [
    {
      icon: FaLocationDot,
      label: "Dirección",
      value: addressLine,
      href: mapsUrl,
      method: "address",
      external: true,
    },
    {
      icon: FaPhone,
      label: "Teléfono",
      value: site.phoneDisplay,
      href: `tel:+${site.phoneRaw}`,
      method: "phone",
    },
    {
      icon: FaWhatsapp,
      label: "WhatsApp",
      value: "Escribinos directo",
      href: whatsappUrl,
      method: "whatsapp",
      external: true,
    },
    {
      icon: FaEnvelope,
      label: "Email",
      value: site.email,
      href: `mailto:${site.email}`,
      method: "email",
    },
  ];

  return (
    <section id="contacto" className="bg-campo-950 py-20 text-white sm:py-28">
      <div className="container-site">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-campo-400">
            Contacto
          </p>
          <h2 className="section-title text-white">
            Contanos qué necesitás regar
          </h2>
          <p className="section-lead text-campo-100/85">
            Respondemos a la brevedad con una propuesta concreta. La consulta y
            el presupuesto no tienen cargo.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-8 lg:grid-cols-5">
          {/* Datos de contacto */}
          <Reveal className="lg:col-span-2">
            <div className="h-full rounded-2xl bg-white/[0.06] p-7 ring-1 ring-white/10">
              <ul className="space-y-6">
                {contactItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.label} className="flex gap-4">
                      <span
                        className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-campo-800 text-campo-300"
                        aria-hidden="true"
                      >
                        <Icon size={16} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wider text-campo-400">
                          {item.label}
                        </p>
                        <a
                          href={item.href}
                          {...(item.external
                            ? { target: "_blank", rel: "noreferrer" }
                            : {})}
                          onClick={() =>
                            trackEvent("contact_click", { method: item.method, location: "contact_section" })
                          }
                          className="mt-1 block text-sm leading-relaxed text-white transition-colors hover:text-campo-300"
                        >
                          {item.value}
                        </a>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-8 border-t border-white/10 pt-6">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-campo-400">
                  <FaClock size={13} aria-hidden="true" />
                  Horarios
                </p>
                <dl className="mt-3 space-y-1.5">
                  {site.hours.map((h) => (
                    <div
                      key={h.days}
                      className="flex justify-between gap-4 text-sm"
                    >
                      <dt className="text-campo-100/70">{h.days}</dt>
                      <dd className="text-right text-white">{h.time}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              {/* Mapa sin cookies ni scripts de terceros */}
              <div className="mt-8 overflow-hidden rounded-xl ring-1 ring-white/10">
                <iframe
                  title="Ubicación de Riego Mercedes en el mapa"
                  loading="lazy"
                  className="h-48 w-full border-0"
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                    site.address.lng - 0.008
                  }%2C${site.address.lat - 0.005}%2C${
                    site.address.lng + 0.008
                  }%2C${site.address.lat + 0.005}&layer=mapnik&marker=${
                    site.address.lat
                  }%2C${site.address.lng}`}
                />
              </div>
            </div>
          </Reveal>

          {/* Formulario */}
          <Reveal delay={120} className="lg:col-span-3">
            <form
              ref={form}
              onSubmit={sendEmail}
              noValidate={false}
              className="rounded-2xl bg-white p-7 shadow-card-hover sm:p-9"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field id="nombre" label="Nombre" required>
                  <input
                    id="nombre"
                    name="nombre"
                    type="text"
                    autoComplete="given-name"
                    required
                    placeholder="Juan"
                    className={inputClass}
                  />
                </Field>

                <Field id="apellido" label="Apellido" required>
                  <input
                    id="apellido"
                    name="apellido"
                    type="text"
                    autoComplete="family-name"
                    required
                    placeholder="Pérez"
                    className={inputClass}
                  />
                </Field>

                <Field id="email" label="Email" required>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="juan@ejemplo.com"
                    className={inputClass}
                  />
                </Field>

                <Field id="telefono" label="Teléfono">
                  <input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    autoComplete="tel"
                    placeholder="2657 50-4891"
                    className={inputClass}
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field id="interes" label="¿Qué te interesa?">
                    <select id="interes" name="interes" className={inputClass}>
                      <option>Pivotes centrales</option>
                      <option>Riego por aspersión</option>
                      <option>Riego por goteo</option>
                      <option>Alas regadoras</option>
                      <option>Parques y jardines</option>
                      <option>Repuestos y service</option>
                      <option>Otra consulta</option>
                    </select>
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <Field id="mensaje" label="Mensaje" required>
                    <textarea
                      id="mensaje"
                      name="mensaje"
                      rows="6"
                      required
                      placeholder="Contanos la superficie a regar, el cultivo y la fuente de agua disponible."
                      className={`${inputClass} resize-y`}
                    />
                  </Field>
                </div>

                {/* Trampa anti-spam: oculta para personas, visible para bots */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="website">No completar</label>
                  <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
                </div>

                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="btn-primary w-full sm:w-auto"
                  >
                    {status === "sending" ? "Enviando…" : "Enviar consulta"}
                  </button>

                  <p className="mt-3 text-xs text-slate-500">
                    Los campos marcados con{" "}
                    <span className="text-campo-700">*</span> son obligatorios.
                  </p>

                  {/* Feedback accesible: los lectores de pantalla lo anuncian */}
                  <div role="status" aria-live="polite">
                    {status === "ok" && (
                      <p className="mt-4 flex items-start gap-2 rounded-lg bg-campo-50 p-4 text-sm text-campo-800">
                        <FaCheck className="mt-0.5 shrink-0" aria-hidden="true" />
                        ¡Listo! Recibimos tu consulta y te vamos a responder a
                        la brevedad.
                      </p>
                    )}
                    {status === "error" && (
                      <p className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-800">
                        <FaTriangleExclamation
                          className="mt-0.5 shrink-0"
                          aria-hidden="true"
                        />
                        <span>
                          No pudimos enviar el mensaje. Probá de nuevo o
                          escribinos por{" "}
                          <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() =>
                              trackEvent("contact_click", { method: "whatsapp", location: "form_error_fallback" })
                            }
                            className="font-semibold underline"
                          >
                            WhatsApp
                          </a>
                          .
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
